import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { ChevronRight } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { EntityType } from "@repo/types/enums";
import type { KeyValueDTO, KeyValuePairWithGroupDTO } from "@repo/types/dtos";
import type { GetPagedDataViewModel } from "../../types/viewModels/dataViews";
import type { Entity, TableStateProps } from "@repo/types/ui/tables";
import type { ColumnKey } from "@repo/types/ui/tables";
import { Table, type TableProps } from "../tables";

export interface DataViewGroupProps<T extends Entity>
  extends TableProps<T>, TableStateProps {
  columns: ColumnDef<T>[];
  keyValuePair: KeyValuePairWithGroupDTO<T>;
  getPagedDataViewModel: GetPagedDataViewModel<T>;
  onHorizontalScroll?: (cb: (scrollLeft: number) => void) => void;
}

export function DataViewGroup<T extends Entity>({
  columns,
  keyValuePair,
  getPagedDataViewModel,
  entityType,
  onCellValueUpdated,
  sorting,
  onSortingChange,
  columnOrder,
  onColumnOrderChange,
  columnSizing,
  onColumnSizingChange,
  columnPinning,
  onHorizontalScroll,
  onColumnPinningChange,
}: DataViewGroupProps<T>) {
  const headerInnerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(keyValuePair.LinkedEntityItemsCount! > 0);
  const viewSearchColumn = useMemo(() => {
    const searchColumn: KeyValueDTO<ColumnKey<T>, string> = {
      Key: keyValuePair.LinkedEntityColumnName!,
      Value: keyValuePair.Id.toString(),
    };

    return searchColumn;
  }, [keyValuePair]);

  const updatedColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      meta: {
        ...col.meta,
        borderColor: keyValuePair?.BgColorHex,
      },
    }));
  }, [columns, keyValuePair?.BgColorHex]);

  function handleGroupToggle() {
    setOpen(!open);
  }

  const rafId = useRef<number | null>(null);

  const handleHorizontalScroll = useCallback((scrollLeft: number) => {
    if (!headerInnerRef.current) return;

    if (rafId.current) cancelAnimationFrame(rafId.current);

    rafId.current = requestAnimationFrame(() => {
      if (!headerInnerRef.current) return;

      headerInnerRef.current.style.transform = `translate3d(${scrollLeft}px, 0, 0)`;
    });
  }, []);

  useEffect(() => {
    if (!onHorizontalScroll) return;

    onHorizontalScroll(handleHorizontalScroll);
  }, [onHorizontalScroll, handleHorizontalScroll]);

  return (
    <div
      className={"grid mb-3 rounded-sm"}
      style={{
        borderLeft: `0px solid ${
          !open ? keyValuePair?.BgColorHex : "transparent"
        }`,
      }}
    >
      <div className="sticky top-0 z-50 bg-white overflow-hidden">
        <div
          ref={headerInnerRef}
          className="flex cursor-pointer px-2 py-1 min-w-max will-change-transform"
          style={{
            transform: "translateZ(0)",
            borderRadius: "3px 0px 0px 3px",
            borderTop: !open ? "1px solid #ccc" : "1px solid transparent",
            borderBottom: !open ? "1px solid #ccc" : "1px solid transparent",
            borderLeft: !open
              ? `6px solid ${keyValuePair?.BgColorHex}`
              : "6px solid transparent",
          }}
          onClick={handleGroupToggle}
        >
          <ChevronRight
            className={`h-5 w-5 mt-1 mr-2 text-gray-500 transition-transform duration-100 hover:cursor-pointer ${
              open ? "rotate-90" : ""
            }`}
            style={{ color: keyValuePair?.BgColorHex }}
          />
          <span className="group text-gray-500">
            <h2
              className="font-medium text-lg"
              style={{ color: keyValuePair?.BgColorHex }}
            >
              {keyValuePair.Value}
              {open && (
                <span className="text-sm text-gray-400 px-2 py-0.5 ml-2 opacity-0 group-hover:opacity-100 transition">
                  {keyValuePair?.LinkedEntityItemsCount}{" "}
                  {EntityType[keyValuePair.PairType!]}
                  {keyValuePair?.LinkedEntityItemsCount !== 1 ? "s" : ""}
                </span>
              )}
            </h2>
            {!open && (
              <span className="text-sm">
                {keyValuePair?.LinkedEntityItemsCount}{" "}
                {EntityType[keyValuePair.PairType!]}
                {keyValuePair?.LinkedEntityItemsCount !== 1 ? "s" : ""}
              </span>
            )}
          </span>
        </div>
      </div>

      {open && (
        <div
          className={"border-l-0 rounded-sm"}
          style={{
            borderColor: keyValuePair?.BgColorHex,
          }}
        >
          <Table<T>
            hasGroup={true}
            columns={updatedColumns}
            getPagedDataViewModel={{
              ...getPagedDataViewModel,
              ColumnFilters: [viewSearchColumn],
            }}
            entityType={entityType}
            sorting={sorting}
            onSortingChange={onSortingChange}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
            columnSizing={columnSizing}
            onColumnSizingChange={onColumnSizingChange}
            columnPinning={columnPinning}
            onColumnPinningChange={onColumnPinningChange}
            onCellValueUpdated={onCellValueUpdated}
          />
        </div>
      )}
    </div>
  );
}
